import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, Linkedin, Facebook, Youtube, Instagram } from 'lucide-react';
import API_BASE_URL from '../config/api';

const Footer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const currentYear = new Date().getFullYear();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      console.log('📤 Sending contact form...', formData);
      
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      console.log('✅ Response received:', data);
      
      if (response.ok) {
        alert(data.message || 'Thanks for submitting! We will call you for a Cup\'a Coffee');
        setFormData({ name: '', email: '', phone: '', message: '' });
        console.log('✅ Contact form submitted successfully!');
      } else {
        alert('Failed to submit form. Please try again.');
        console.error('❌ Server error:', data);
      }
    } catch (error) {
      console.error('❌ Error submitting form:', error);
      alert('❌ Failed to submit. Please try again later.');
    }
  };

  return (
    <>
      {/* Horizontal line before footer */}
      <div className="border-t border-black"></div>
      
      <footer id="contact" className="bg-[#b8d4d4] text-gray-800 py-16">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
            
            {/* Column 1: Contact Form */}
            <div className="pr-6">
              <h3 className="text-2xl font-black mb-4 text-gray-900" style={{ fontFamily: "'Diplomata', 'Arbutus', cursive" }}>
                Write to us
              </h3>
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 text-sm rounded-lg bg-white border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-all"
                  style={{ fontFamily: "'Hepta Slab', serif" }}
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 text-sm rounded-lg bg-white border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-all"
                  style={{ fontFamily: "'Hepta Slab', serif" }}
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 text-sm rounded-lg bg-white border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-all"
                  style={{ fontFamily: "'Hepta Slab', serif" }}
                />
                <textarea
                  name="message"
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="3"
                  className="w-full px-3 py-2 text-sm rounded-lg bg-white border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-all resize-none"
                  style={{ fontFamily: "'Hepta Slab', serif" }}
                />
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-4 py-2 text-sm rounded-lg font-bold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  style={{ fontFamily: "'Hepta Slab', serif" }}
                >
                  <Send className="w-4 h-4" />
                  Send Message
                </button>
              </form>
            </div>

            {/* Column 2: Office Locations */}
            <div className="flex justify-center px-6">
              <div className="text-left max-w-md">
                <h3 className="text-2xl font-black mb-4 text-gray-900" style={{ fontFamily: "'Diplomata', 'Arbutus', cursive" }}>
                  Locations
                </h3>
                <div className="space-y-6">
                  {/* India Office - Bangalore */}
                  <div>
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-bold text-base mb-2 text-gray-900" style={{ fontFamily: "'Hepta Slab', serif" }}>
                           Office
                        </h4>
                        <p className="text-gray-700 text-sm leading-relaxed" style={{ fontFamily: "'Hepta Slab', serif" }}>
                          Marathahalli Outer Ring Road<br />
                          Varthur Hobli<br />
                          Bangalore, Karnataka 560103, IN
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Column 3: Contact Details */}
            <div className="pl-6">
              <h3 className="text-2xl font-black mb-4 text-gray-900" style={{ fontFamily: "'Diplomata', 'Arbutus', cursive" }}>
                Call Us
              </h3>
              <div className="space-y-4">
                {/* Phone */}
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1 text-sm text-gray-900" style={{ fontFamily: "'Hepta Slab', serif" }}>
                      Phone
                    </h4>
                    <a 
                      href="tel:+917676032315" 
                      className="text-gray-700 hover:text-orange-500 transition-colors text-sm"
                      style={{ fontFamily: "'Hepta Slab', serif" }}
                    >
                      +91 76760 32315
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1 text-sm text-gray-900" style={{ fontFamily: "'Hepta Slab', serif" }}>
                      Email
                    </h4>
                    <a 
                      href="mailto:support@omniflairglobal.com" 
                      className="text-gray-700 hover:text-orange-500 transition-colors text-sm"
                      style={{ fontFamily: "'Hepta Slab', serif" }}
                    >
                      support@omniflairglobal.com
                    </a>
                  </div>
                </div>

                {/* Website */}
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1 text-sm text-gray-900" style={{ fontFamily: "'Hepta Slab', serif" }}>
                      Website
                    </h4>
                    <a 
                      href="https://share.google/vs1cvxPOv1FeAzElJ" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-700 hover:text-orange-500 transition-colors text-sm"
                      style={{ fontFamily: "'Hepta Slab', serif" }}
                    >
                      www.omnifairglobal.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Horizontal line before social media */}
          <div className="border-t border-black mt-12 pt-8">
            {/* Social Media Icons */}
            <div className="flex justify-center gap-6 mb-6">
              <a 
                href="https://www.linkedin.com/company/omniflair-events/?viewAsMember=true" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-orange-500 transition-colors transform hover:scale-110 duration-300"
              >
                <Linkedin size={28} />
              </a>
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-orange-500 transition-colors transform hover:scale-110 duration-300"
              >
                <Facebook size={28} />
              </a>
              <a 
                href="https://youtube.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-orange-500 transition-colors transform hover:scale-110 duration-300"
              >
                <Youtube size={28} />
              </a>
              <a 
                href="https://www.instagram.com/omniflairglobal?utm_source=qr&igsh=MW84MWY3aHVmYjRrNA%3D%3D" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-orange-500 transition-colors transform hover:scale-110 duration-300"
              >
                <Instagram size={28} />
              </a>
            </div>

            {/* Copyright */}
            <div className="text-center">
              <p className="text-gray-700 text-sm" style={{ fontFamily: "'Hepta Slab', serif" }}>
                {currentYear} ALL COPYRIGHTS RESERVED TO OMNIFAIRGLOBAL
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;